import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocalizedNavigate } from '@/shared/hooks'
import { toast } from 'react-toastify'
import { MessageCircle, Download, ArrowRight, Loader2 } from 'lucide-react'
import { useCreateVersion } from '@/features/scoping-v2/hooks'
import { cn } from '@/shared/lib/utils'
import type { TeamWithActions } from '@/shared/types'

type CardVariant = 'active' | 'completed' | 'archived'

interface ProjectCardV2Props {
  team: TeamWithActions
  variant?: CardVariant
}

function StatusBadge({ variant }: { variant: CardVariant }) {
  const { t } = useTranslation()

  const styles: Record<CardVariant, { bg: string; text: string; label: string }> = {
    active: {
      bg: 'bg-accent-light',
      text: 'text-accent',
      label: t('dashboard.card.scoping'),
    },
    completed: {
      bg: 'bg-green-100',
      text: 'text-green-600',
      label: t('dashboard.card.specsReady'),
    },
    archived: {
      bg: 'bg-background-secondary',
      text: 'text-foreground-muted',
      label: t('dashboard.card.archived'),
    },
  }

  const style = styles[variant]

  return (
    <span className={cn('rounded-md px-2.5 py-1 text-xs font-medium', style.bg, style.text)}>
      {style.label}
    </span>
  )
}

function formatTimeAgo(date: string, t: (key: string, options?: Record<string, unknown>) => string): string {
  const now = new Date()
  const then = new Date(date)
  const diffMs = now.getTime() - then.getTime()
  const diffMins = Math.floor(diffMs / (1000 * 60))
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffMins < 60) {
    return t('dashboard.card.updatedMinutes', { count: diffMins || 1 })
  }
  if (diffHours < 24) {
    return t('dashboard.card.updatedHours', { count: diffHours })
  }
  if (diffDays === 1) {
    return t('dashboard.card.updatedYesterday')
  }
  if (diffDays < 7) {
    return t('dashboard.card.updatedDays', { count: diffDays })
  }
  return then.toLocaleDateString()
}

export function ProjectCardV2({ team, variant = 'active' }: ProjectCardV2Props) {
  const { t } = useTranslation()
  const navigate = useLocalizedNavigate()
  const createVersionMutation = useCreateVersion()
  const [isCreatingVersion, setIsCreatingVersion] = useState(false)

  const project = team.active_project
  if (!project) return null

  const handleContinue = () => {
    navigate(`/scoping/${project.short_id}`)
  }

  const handleDownload = () => {
    if (project.specs_pdf_url) {
      window.open(project.specs_pdf_url, '_blank')
    }
  }

  const handleRequestNewVersion = async () => {
    if (!project.id || isCreatingVersion) return
    setIsCreatingVersion(true)
    try {
      const result = await createVersionMutation.mutateAsync({
        projectId: project.id,
        currentVersion: project.current_version,
      })
      toast.success(t('postScoping.versions.createSuccess', { version: result.newVersionNumber }))
      navigate(`/scoping/${project.short_id}`)
    } catch {
      toast.error(t('postScoping.versions.createError'))
    } finally {
      setIsCreatingVersion(false)
    }
  }

  const handleViewKanban = () => {
    navigate(`/p/${project.short_id}`)
  }

  const isActive = variant === 'active'
  const isCompleted = variant === 'completed'

  return (
    <div
      className={cn(
        'flex flex-col gap-4 rounded-xl border bg-background p-6',
        isActive && 'border-2 border-accent',
        !isActive && 'border-border',
        variant === 'archived' && 'opacity-70'
      )}
    >
      {/* Header */}
      <div className="flex items-start justify-between">
        <h3 className="text-lg font-semibold text-foreground">{project.name}</h3>
        <StatusBadge variant={variant} />
      </div>

      {/* Description */}
      {project.description && (
        <p className="line-clamp-2 text-sm text-foreground-muted">
          {project.description}
        </p>
      )}

      {/* Meta: Version + Last activity */}
      <div className="flex items-center gap-4">
        <span className={cn(
          'text-xs font-medium',
          isCompleted ? 'text-green-600' : 'text-foreground-muted'
        )}>
          V{project.current_version} • {isCompleted ? t('dashboard.card.final') : t('dashboard.card.draft')}
        </span>
        <span className="text-xs text-foreground-subtle">
          {formatTimeAgo(project.updated_at, t)}
        </span>
      </div>

      {/* Progress bar - only for active */}
      {isActive && (
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-border">
          <div
            className="h-full rounded-full bg-accent transition-all"
            style={{ width: '35%' }}
          />
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-3">
        {isActive && (
          <button
            onClick={handleContinue}
            className={cn(
              'flex items-center gap-2 rounded-lg bg-accent px-4 py-2.5',
              'text-sm font-semibold text-white transition-colors hover:bg-accent-hover'
            )}
          >
            <MessageCircle className="h-4 w-4" />
            {t('dashboard.card.continueWithNicolAI')}
          </button>
        )}

        {isCompleted && (
          <>
            <button
              onClick={handleDownload}
              className={cn(
                'flex items-center gap-2 rounded-lg bg-primary px-4 py-2.5',
                'text-sm font-semibold text-white transition-colors hover:bg-primary-hover'
              )}
            >
              <Download className="h-4 w-4" />
              {t('dashboard.card.downloadPdf')}
            </button>
            <button
              onClick={handleRequestNewVersion}
              disabled={isCreatingVersion}
              className={cn(
                'flex items-center gap-2 rounded-lg border border-border px-4 py-2.5',
                'text-sm font-medium text-foreground transition-colors',
                isCreatingVersion ? 'cursor-not-allowed opacity-70' : 'hover:bg-background-secondary'
              )}
            >
              {isCreatingVersion && <Loader2 className="h-4 w-4 animate-spin" />}
              {t('postScoping.versions.requestNew', { version: project.current_version + 1 })}
            </button>
          </>
        )}

        {variant === 'archived' && (
          <button
            onClick={handleViewKanban}
            className={cn(
              'flex items-center gap-2 rounded-lg border border-border px-4 py-2.5',
              'text-sm font-medium text-foreground transition-colors hover:bg-background-secondary'
            )}
          >
            {t('dashboard.card.viewSpecs')}
            <ArrowRight className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  )
}
